import { useEffect, useMemo, useState } from 'react'
import { listReleases, triggerAgentUpdate } from '@/api/releases'
import type { AgentReleaseSummary } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'

interface Props {
  nodeId: string
  nodeName: string
  currentVersion: string | null
  onClose: () => void
  onSuccess: (msg: string) => void
}

export default function AgentUpdateModal({ nodeId, nodeName, currentVersion, onClose, onSuccess }: Props) {
  const [releases, setReleases] = useState<AgentReleaseSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<string>('')
  const [force, setForce] = useState(false)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listReleases()
      .then((res) => {
        if (cancelled) return
        // newest first; backend already orders by uploaded_at desc
        setReleases(res.items)
        // Default selection: latest release that differs from current
        const firstDifferent = res.items.find((r) => r.version !== currentVersion)
        if (firstDifferent) setSelected(firstDifferent.version)
        else if (res.items.length > 0) setSelected(res.items[0].version)
      })
      .catch((e: { message?: string }) =>
        setError(e?.message ?? '릴리즈 목록을 불러올 수 없습니다.'),
      )
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [currentVersion])

  const selectedRelease = useMemo(
    () => releases.find((r) => r.version === selected) ?? null,
    [releases, selected],
  )

  const sameAsCurrent: boolean = !!selectedRelease && selectedRelease.version === currentVersion
  const probablyDowngrade: boolean =
    !!selectedRelease &&
    !!currentVersion &&
    compareSemverLoose(selectedRelease.version, currentVersion) < 0

  async function handleSubmit() {
    if (!selected) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const res = await triggerAgentUpdate(nodeId, {
        version: selected,
        force: probablyDowngrade ? force : false,
        reason: reason.trim() || null,
      })
      onSuccess(
        `agent.update 명령 생성: ${selected} (command=${res.id.slice(0, 8)}…). ` +
          `1~3분 안에 heartbeat 에 새 버전 반영되면 자동 완료됩니다.`,
      )
    } catch (e: unknown) {
      const err = e as { message?: string }
      setSubmitError(err?.message ?? '명령 생성 실패')
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={() => !submitting && onClose()}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 24,
          maxWidth: 520,
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, marginBottom: 6, fontSize: 16 }}>에이전트 업데이트</h3>
        <p style={{ fontSize: 12, color: '#666', marginTop: 0, marginBottom: 16 }}>
          노드: <strong>{nodeName}</strong> · 현재 버전:{' '}
          <code style={pill}>{currentVersion ?? '알 수 없음'}</code>
        </p>

        {error && (
          <div style={errorBox}>{error}</div>
        )}

        {loading ? (
          <div style={{ color: '#888', padding: 12 }}>릴리즈 목록 로딩 중…</div>
        ) : releases.length === 0 ? (
          <div style={{ padding: 12, background: '#fff8e1', color: '#8d6e00', borderRadius: 4, fontSize: 13 }}>
            등록된 릴리즈가 없습니다. 먼저 <strong>Releases</strong> 페이지에서 .deb 를 업로드하세요.
          </div>
        ) : (
          <>
            <label style={label}>
              대상 버전
              <select
                value={selected}
                onChange={(e) => {
                  setSelected(e.target.value)
                  setForce(false)
                }}
                disabled={submitting}
                style={input}
              >
                {releases.map((r) => (
                  <option key={r.version} value={r.version}>
                    {r.version} ({r.channel}) — {formatTime(r.uploaded_at)}
                    {r.version === currentVersion ? '  ← 현재' : ''}
                  </option>
                ))}
              </select>
            </label>

            {selectedRelease && (
              <div style={{ fontSize: 11, color: '#666', marginTop: -4, marginBottom: 12, fontFamily: 'ui-monospace' }}>
                sha256: {selectedRelease.sha256.slice(0, 16)}… · {formatSize(selectedRelease.size_bytes)}
                {selectedRelease.notes && (
                  <div style={{ marginTop: 4, fontFamily: 'inherit', color: '#888' }}>
                    {selectedRelease.notes}
                  </div>
                )}
              </div>
            )}

            {(sameAsCurrent || probablyDowngrade) && (
              <div
                style={{
                  padding: 10,
                  background: '#fff8e1',
                  color: '#8d6e00',
                  borderRadius: 4,
                  fontSize: 12,
                  marginBottom: 12,
                }}
              >
                {sameAsCurrent ? (
                  <span>같은 버전은 업데이트할 수 없습니다. 새 패치 버전을 등록하세요.</span>
                ) : (
                  <span>다운그레이드입니다 ({currentVersion} → {selectedRelease!.version}). 정말 진행하려면 <strong>force</strong> 체크.</span>
                )}
              </div>
            )}

            {probablyDowngrade && !sameAsCurrent && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#c62828', marginBottom: 12 }}>
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  disabled={submitting}
                />
                force=true (다운그레이드 검증 우회)
              </label>
            )}

            <label style={label}>
              사유 (선택, audit log 에 기록)
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
                placeholder="예: 0.2.1 hotfix 적용"
                style={input}
              />
            </label>

            {submitError && <div style={errorBox}>{submitError}</div>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={onClose}
                disabled={submitting}
                style={btnSecondary}
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  !selected ||
                  sameAsCurrent ||
                  (probablyDowngrade && !force)
                }
                style={{
                  ...btnPrimary,
                  background: submitting ? '#999' : btnPrimary.background,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? '생성 중…' : '업데이트 트리거'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// --- helpers ---

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatTime(iso: string): string {
  return formatKstDateTime(iso, iso)
}

// Permissive SemVer-ish compare. Splits on dots, compares numeric segments
// numerically and falls back to string compare on non-numeric trailers.
// Returns negative if a < b, 0 if equal, positive if a > b.
function compareSemverLoose(a: string, b: string): number {
  const pa = a.split(/[.\-+]/)
  const pb = b.split(/[.\-+]/)
  const len = Math.max(pa.length, pb.length)
  for (let i = 0; i < len; i++) {
    const sa = pa[i] ?? '0'
    const sb = pb[i] ?? '0'
    const na = Number(sa)
    const nb = Number(sb)
    const bothNumeric = !Number.isNaN(na) && !Number.isNaN(nb)
    if (bothNumeric) {
      if (na !== nb) return na - nb
    } else {
      if (sa !== sb) return sa < sb ? -1 : 1
    }
  }
  return 0
}

// --- styles ---

const label: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: '#555',
  marginBottom: 12,
}
const input: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  marginTop: 4,
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
}
const btnPrimary: React.CSSProperties = {
  padding: '6px 14px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}
const btnSecondary: React.CSSProperties = {
  padding: '6px 14px',
  background: '#f0f0f0',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
}
const errorBox: React.CSSProperties = {
  padding: 10,
  background: '#fdecea',
  color: '#c62828',
  borderRadius: 4,
  marginBottom: 12,
  fontSize: 12,
}
const pill: React.CSSProperties = {
  background: '#f5f5f7',
  padding: '1px 6px',
  borderRadius: 3,
  fontFamily: 'ui-monospace',
}
